import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export const generateAuctionProposal = async (artwork, auctionHouse, message, meta = {}) => {
  const element = document.createElement('div');
  element.innerHTML = getProposalHTML(artwork, auctionHouse, message, meta);
  document.body.appendChild(element);

  try {
    // Attendre que le DOM soit bien prêt (images + polices)
    await new Promise(resolve => setTimeout(resolve, 100));

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      onclone: (doc) => {
        const container = doc.querySelector('.proposal-container');
        if (container) {
          container.style.width = '800px';
          container.style.padding = '20px';
          container.style.margin = '0 auto';
        }
      }
    });

    const imgData = canvas.toDataURL('image/jpeg', 1.0);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const pdfBlob = pdf.output('blob');
    document.body.removeChild(element);
    return pdfBlob;
  } catch (error) {
    console.error('Erreur lors de la génération du PDF:', error);
    document.body.removeChild(element);
    throw error;
  }
};

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR');
};

const DOCUMENT_LABELS = {
  images: 'Photographies haute définition',
  invoice: 'Facture / preuve d’acquisition',
  provenance: 'Historique de provenance',
  condition: 'Constat d’état',
  authenticity: 'Certificat d’authenticité',
  valuation: 'Estimation / valorisation',
};

const getProposalHTML = (artwork, auctionHouse, message, meta = {}) => {
  const mainImage = Array.isArray(artwork.images) && artwork.images.length > 0
    ? artwork.images.find(img => img.isPrimary) || artwork.images[0]
    : null;

  const mainImageUrl = mainImage 
    ? mainImage.url || mainImage.path 
    : 'https://via.placeholder.com/400x400?text=Aucune+image';

  const dimensions = artwork.specifications?.dimensions || {};
  const framedSize = artwork.specifications?.framedSize || {};
  const paperSize = artwork.specifications?.paperSize || {};
  const unit = dimensions.unit || 'cm';

  const formatDimensions = (dim) => {
    if (!dim || (!dim.height && !dim.width && !dim.depth)) return 'Non spécifiées';
    let result = '';
    if (dim.height) result += `H: ${dim.height} ${unit}`;
    if (dim.width) result += result ? `, L: ${dim.width} ${unit}` : `L: ${dim.width} ${unit}`;
    if (dim.depth) result += result ? `, P: ${dim.depth} ${unit}` : `P: ${dim.depth} ${unit}`;
    return result || 'Non spécifiées';
  };

  const formatProvenance = () => {
    if (!Array.isArray(artwork.provenance) || artwork.provenance.length === 0) {
      return '<p>Aucune information de provenance disponible</p>';
    }

    return artwork.provenance
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .map(prov => `
        <div class="provenance-item">
          <div class="provenance-date">${formatDate(prov.date)}</div>
          <div>${prov.description}</div>
          ${prov.owner ? `<div>Propriétaire: ${prov.owner}</div>` : ''}
          ${prov.location ? `<div>Lieu: ${prov.location}</div>` : ''}
          ${prov.acquisitionMethod ? `<div>Mode d'acquisition: ${prov.acquisitionMethod}</div>` : ''}
        </div>
      `).join('');
  };

  const formatDocuments = () => {
    if (!Array.isArray(artwork.documents) || artwork.documents.length === 0) {
      return '<li>Aucun document disponible</li>';
    }

    return artwork.documents
      .map(doc => `<li>${doc.title || doc.name || 'Document sans titre'} (${doc.type || 'Type non spécifié'})</li>`)
      .join('');
  };

  const editionInfo = artwork.isEdition
    ? (artwork.editionInfo || 'Œuvre en édition limitée (détails non spécifiés)')
    : 'Œuvre unique';

  const condition = artwork.condition || 'Non spécifié';
  const conditionNotes = artwork.conditionNotes || 'Aucune note spécifique sur l\'état de conservation';

  return `
  <style>
    body { font-family: Inter, sans-serif; color: #333; padding: 20px; }
    .proposal-container { max-width: 800px; margin: auto; background: #fff; }
    .header { text-align: center; border-bottom: 1px solid #ddd; padding-bottom: 15px; margin-bottom: 20px; }
    h1 { font-family: 'Playfair Display', serif; font-size: 24px; }
    .artwork-details { display: flex; margin-bottom: 20px; }
    .artwork-image img { max-width: 100%; border-radius: 4px; }
    .info-group { margin-bottom: 10px; }
    .info-label { font-weight: bold; }
    .specifications td { padding: 5px; }
    .condition-notes { background: #fff9f0; padding: 10px; border-left: 3px solid #f0c78a; }
    .documents-list li::before { content: "• "; color: #999; }
    .footer { font-size: 12px; text-align: center; margin-top: 40px; color: #999; border-top: 1px solid #eee; padding-top: 10px; }
  </style>
  <div class="proposal-container">
    <div class="header">
      <h1>Proposition pour vente aux enchères</h1>
      <div>${auctionHouse}</div>
      <p>Date: ${formatDate(new Date())}</p>
    </div>
    <div class="artwork-details">
      <div class="artwork-image"><img src="${mainImageUrl}" alt="Image de l'œuvre" /></div>
      <div class="artwork-info">
        <div class="info-group"><div class="info-label">Titre:</div> ${artwork.title || 'Sans titre'}</div>
        <div class="info-group"><div class="info-label">Artiste:</div> ${artwork.artist || 'Artiste inconnu'}</div>
        <div class="info-group"><div class="info-label">Année:</div> ${artwork.year || 'Non spécifiée'}</div>
        <div class="info-group"><div class="info-label">Medium:</div> ${artwork.medium || 'Non spécifié'}</div>
        <div class="info-group"><div class="info-label">Type:</div> ${artwork.typeOfArt || 'Non spécifié'}</div>
      </div>
    </div>
    <h2>Spécifications</h2>
    <table class="specifications">
      <tr><td>Dimensions (sans cadre)</td><td>${formatDimensions(dimensions)}</td></tr>
      <tr><td>Dimensions (avec cadre)</td><td>${formatDimensions(framedSize)}</td></tr>
      <tr><td>Dimensions du papier</td><td>${formatDimensions(paperSize)}</td></tr>
      <tr><td>Édition</td><td>${editionInfo}</td></tr>
      <tr><td>Sujet</td><td>${artwork.subjectMatter || 'Non spécifié'}</td></tr>
    </table>
    <h2>Estimation</h2>
    <p class="price">${
      meta.estimationValue || artwork.value
        ? Number(meta.estimationValue || artwork.value).toLocaleString('fr-FR')
        : '0'
    } €</p>
    ${
      meta.reserveValue
        ? `<p>Prix de réserve : <strong>${Number(meta.reserveValue).toLocaleString('fr-FR')} €</strong> <span style="color:#999;">(prix plancher en dessous duquel l’œuvre n’est pas vendue)</span></p>`
        : ''
    }
    ${
      meta.provenanceSummary
        ? `<h2>Synthèse de provenance</h2><p>${meta.provenanceSummary}</p>`
        : ''
    }
    <h2>Provenance</h2>
    ${formatProvenance()}
    ${
      typeof meta.ownershipConfirmed === 'boolean'
        ? `<p style="margin-top:8px;">Propriété : ${
            meta.ownershipConfirmed
              ? 'le proposant certifie être propriétaire de l’œuvre.'
              : 'non confirmée.'
          }</p>`
        : ''
    }
    <h2>État de conservation</h2>
    <p>${condition}</p>
    <div class="condition-notes"><p>${conditionNotes}</p></div>
    <h2>Documents</h2>
    <ul class="documents-list">${
      Array.isArray(meta.documentsReady) && meta.documentsReady.length
        ? meta.documentsReady.map((d) => `<li>${DOCUMENT_LABELS[d] || d}</li>`).join('')
        : formatDocuments()
    }</ul>
    <h2>Note d’intention</h2>
    <p>${message || 'Aucune note fournie.'}</p>
    <div class="footer">Document généré le ${formatDate(new Date())}</div>
  </div>`;
};

export const downloadAuctionProposal = async (artwork, auctionHouse, message, filename = 'proposition-vente-encheres.pdf') => {
  try {
    let meta = {};
    // Nouvelle signature : un seul objet payload { artwork, institution, message, additionalData }
    if (artwork && typeof artwork === 'object' && ('additionalData' in artwork || 'proposalType' in artwork)) {
      const payload = artwork;
      const add = payload.additionalData || {};
      artwork = payload.artwork || {};
      auctionHouse = payload.institution || '';
      message = payload.message || '';
      meta = {
        estimationValue: add.estimationValue,
        reserveValue: add.reserveValue,
        provenanceSummary: add.provenanceSummary,
        ownershipConfirmed: add.ownershipConfirmed,
        documentsReady: add.documentsReady,
      };
    }
    const pdfBlob = await generateAuctionProposal(artwork, auctionHouse, message, meta);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(pdfBlob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return true;
  } catch (error) {
    console.error('Erreur lors du téléchargement du PDF:', error);
    return false;
  }
};
